/**
 * Utility functions for handling API errors in a consistent way
 */

type ApiErrorResponse = {
  error: string;
  details?: string;
  [key: string]: any;
};

/**
 * Handles API errors and provides consistent error messages
 * @param error The error object
 * @param fallbackMessage A fallback message if no specific error is found
 * @returns A user-friendly error message
 */
export async function handleApiError(error: unknown, fallbackMessage = 'An unexpected error occurred'): Promise<string> {
  // If it's a Response object from fetch
  if (error instanceof Response) {
    try {
      // Try to parse the response as JSON
      const data = await error.clone().json().catch(() => ({})) as ApiErrorResponse;
      return data.error || `Error ${error.status}: ${error.statusText}`;
    } catch {
      return `Error ${error.status}: ${error.statusText}`;
    }
  }
  
  // If it's an Error object
  if (error instanceof Error) {
    // Network errors
    if (error.message.includes('fetch') || error.message.includes('network')) {
      return 'Network error. Please check your connection and try again.';
    }
    
    // Timeout errors
    if (error.message.includes('timeout')) {
      return 'Request timed out. Please try again later.';
    }
    
    return error.message;
  }
  
  // If it's a string
  if (typeof error === 'string') {
    return error;
  }
  
  // If it's an object that might contain an error message
  if (error && typeof error === 'object') {
    const errorObj = error as Record<string, any>;
    
    if (errorObj.error) {
      return typeof errorObj.error === 'string' 
        ? errorObj.error 
        : JSON.stringify(errorObj.error);
    }
    
    if (errorObj.message) {
      return typeof errorObj.message === 'string' 
        ? errorObj.message 
        : JSON.stringify(errorObj.message);
    }
  }
  
  // Default fallback
  return fallbackMessage;
}

/**
 * Creates a retry function with exponential backoff
 * @param fn The function to retry
 * @param maxRetries Maximum number of retries
 * @param baseDelay Base delay in milliseconds
 * @returns A function that will retry the original function
 */
export function withRetry<T>(
  fn: () => Promise<T>, 
  maxRetries = 3, 
  baseDelay = 300
): () => Promise<T> {
  return async function retryFn(): Promise<T> {
    let lastError: unknown;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;
        
        // Don't retry certain errors
        if (error instanceof Response) {
          // Don't retry 4xx client errors (except 429 too many requests)
          if (error.status >= 400 && error.status < 500 && error.status !== 429) {
            throw error;
          }
        }
        
        // If this was the last attempt, throw the error
        if (attempt === maxRetries) {
          throw error;
        }
        
        // Calculate delay with exponential backoff and jitter
        const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 100;
        
        // Wait before next attempt
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    // This should never happen, but TypeScript needs it
    throw lastError;
  };
}
